"use client";

import Fuse from "fuse.js";
import * as React from "react";
import Icon from "@/components/library/Icon";
import InputLabel from "@/components/library/InputLabel";
import {Button} from "@/components/ui/button";
import {
   Command,
   CommandEmpty,
   CommandGroup,
   CommandInput,
   CommandItem,
   CommandList,
   CommandSeparator,
} from "@/components/ui/command";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";

export type ComboboxOption = {
   value: string;
   label: string;
   icon?: React.ReactNode;
   key?: string;
   /** Additional searchable text that won't be displayed (e.g., email, description) */
   searchableDetails?: string;
};

export type ComboboxGroup = {
   label: string;
   options: ComboboxOption[];
};

type ComboboxBaseProps = {
   readonly value?: string;
   readonly initialValue?: string;
   readonly onChange: (value: string) => void;
   readonly placeholder?: string;
   readonly emptyMessage?: string;
   readonly className?: string;
   readonly triggerClassName?: string;
   readonly contentClassName?: string;
   readonly disabled?: boolean;
   readonly allowsCustomValue?: boolean;
   readonly onCustomValueAdd?: (value: string) => void;
   readonly label?: string;
   readonly labelTooltip?: React.ReactNode;
   readonly required?: boolean;
   /** Names the control where no visible `label` does, such as a row in a list. */
   readonly "aria-label"?: string;
   /** `required` only draws a `*`; this is what a screen reader announces. */
   readonly "aria-required"?: boolean;
   readonly id?: string;
   /** When the dropdown opens with no selected value, scroll to this option's position */
   readonly defaultScrollToValue?: string;
};

type ComboboxWithOptions = ComboboxBaseProps & {
   /** Flat list of options */
   readonly options: ComboboxOption[];
   readonly groups?: never;
};

type ComboboxWithGroups = ComboboxBaseProps & {
   readonly options?: never;
   /** Grouped options with section headings */
   readonly groups: ComboboxGroup[];
};

type ComboboxProps = ComboboxWithOptions | ComboboxWithGroups;

export function Combobox({
   options,
   groups,
   value: controlledValue,
   initialValue,
   onChange,
   placeholder,
   emptyMessage,
   className,
   triggerClassName,
   contentClassName,
   disabled,
   allowsCustomValue,
   onCustomValueAdd,
   label,
   labelTooltip,
   required,
   "aria-label": ariaLabel,
   "aria-required": ariaRequired,
   id,
   defaultScrollToValue,
}: Readonly<ComboboxProps>) {
   const [open, setOpen] = React.useState(false);
   const [searchValue, setSearchValue] = React.useState("");
   const [customOptions, setCustomOptions] = React.useState<ComboboxOption[]>([]);
   const deferredSearch = React.useDeferredValue(searchValue);
   const isControlled = controlledValue !== undefined;
   const [uncontrolledValue, setUncontrolledValue] = React.useState(initialValue ?? "");
   const value = isControlled ? controlledValue : uncontrolledValue;
   const generatedId = React.useId();
   const comboboxId = id || `combobox-${generatedId}`;
   const listRef = React.useRef<HTMLDivElement>(null);

   // Scroll to selected item (or defaultScrollToValue) when dropdown opens
   React.useEffect(() => {
      if (!open || !listRef.current) return;
      if (!value && !defaultScrollToValue) return;

      const timeoutId = setTimeout(() => {
         const list = listRef.current;
         if (!list) return;

         let target: HTMLElement | null = list.querySelector('[aria-selected="true"]');

         if (!target && defaultScrollToValue) {
            target = list.querySelector(
               `[data-combobox-value="${CSS.escape(defaultScrollToValue)}"]`
            ) as HTMLElement | null;
         }

         if (target) {
            const listRect = list.getBoundingClientRect();
            const itemRect = target.getBoundingClientRect();
            const itemOffsetTop = target.offsetTop;
            const centerOffset = itemOffsetTop - (listRect.height / 2) + (itemRect.height / 2);
            list.scrollTop = Math.max(0, centerOffset);
         }
      }, 50);
      return () => clearTimeout(timeoutId);
   }, [open, value, defaultScrollToValue]);

   // Determine if we're using grouped or flat options
   const isGrouped = groups && groups.length > 0;

   // Flatten groups to get all options (for search and lookup)
   const flatOptionsFromGroups = React.useMemo(() => {
      if (!groups) return [];
      return groups.flatMap((group) => group.options);
   }, [groups]);

   // Merge provided options with custom options
   const allOptions = React.useMemo(() => {
      const baseOptions = isGrouped ? flatOptionsFromGroups : options || [];
      const merged = [...baseOptions, ...customOptions];
      // Remove duplicates based on value
      const seen = new Set<string>();
      return merged.filter((option) => {
         if (seen.has(option.value)) {
            return false;
         }
         seen.add(option.value);
         return true;
      });
   }, [options, groups, isGrouped, flatOptionsFromGroups, customOptions]);

   // Helper to get the key for an option (key || value)
   const getOptionKey = (option: (typeof allOptions)[number]) => option.key || option.value;

   // Create Fuse instance for fuzzy searching
   const fuse = React.useMemo(() => {
      return new Fuse(allOptions, {
         keys: ["value", "label", "searchableDetails"],
         threshold: 0.3,
         ignoreLocation: true,
         minMatchCharLength: 1,
      });
   }, [allOptions]);

   // Filter options based on search value using fuse.js
   const filteredOptions = React.useMemo(() => {
      if (!deferredSearch.trim()) {
         return allOptions;
      }
      const results = fuse.search(deferredSearch);
      return results.map((result) => result.item);
   }, [deferredSearch, fuse, allOptions]);

   // Check if search value matches any existing option
   const exactMatch = React.useMemo(() => {
      if (!deferredSearch.trim()) return false;
      return filteredOptions.some(
         (option) => option.value.toLowerCase() === deferredSearch.trim().toLowerCase()
      );
   }, [deferredSearch, filteredOptions]);

   // Show plus icon when allowsCustomValue is true, there's a search value, and it doesn't exactly match
   const showAddButton = allowsCustomValue && searchValue.trim().length > 0 && !exactMatch;

   const handleAddCustomValue = () => {
      const customValue = searchValue.trim();
      if (customValue) {
         // Add custom option to the list
         const newCustomOption: ComboboxOption = {
            value: customValue,
            label: customValue,
         };
         setCustomOptions((prev) => {
            // Check if this custom value already exists
            if (prev.some((opt) => opt.value === customValue)) {
               return prev;
            }
            return [...prev, newCustomOption];
         });

         if (!isControlled) {
            setUncontrolledValue(customValue);
         }
         onChange(customValue);
         if (onCustomValueAdd) {
            onCustomValueAdd(customValue);
         }
         setSearchValue("");
         setOpen(false);
      }
   };

   return (
      <div className={cn("w-full", className)}>
         {label && (
            <InputLabel
               htmlFor={comboboxId}
               value={required ? `${label} *` : label}
               tooltipText={labelTooltip}
            />
         )}
         <Popover
            open={open}
            onOpenChange={(newOpen) => {
               setOpen(newOpen);
               if (!newOpen) {
                  setSearchValue("");
               }
            }}
         >
            <PopoverTrigger asChild>
               <Button
                  type="button"
                  id={comboboxId}
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  aria-label={ariaLabel}
                  aria-required={ariaRequired}
                  disabled={disabled}
                  className={cn(
                     "justify-between w-[200px] gap-2",
                     "!bg-white",
                     "dark:!bg-gray_comp",
                     "!border-neutral",
                     "!h-[calc(2.75rem-2px)]",
                     "pl-3",
                     triggerClassName
                  )}
               >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                     {value && allOptions.find((option) => option.value === value)?.icon && (
                        <span className="shrink-0">
                           {allOptions.find((option) => option.value === value)?.icon}
                        </span>
                     )}
                     <span className="truncate text-left">
                        {value
                           ? allOptions.find((option) => option.value === value)?.label || value
                           : placeholder}
                     </span>
                  </div>
                  <span className="flex-shrink-0 ml-1 text-400 text-[10px] [&_svg]:!w-[10px] [&_svg]:!h-[10px]">
                     <Icon icon="chevron-down" />
                  </span>
               </Button>
            </PopoverTrigger>
            <PopoverContent
               portalToBody
               className={cn(
                  "bg-surface p-0 w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]",
                  contentClassName
               )}
            >
               <Command shouldFilter={false}>
                  <div className="relative">
                     <CommandInput
                        placeholder={placeholder}
                        className="border-none focus:ring-0 h-9"
                        value={searchValue}
                        onValueChange={setSearchValue}
                     />
                     {showAddButton && (
                        <button
                           type="button"
                           onClick={handleAddCustomValue}
                           className={cn(
                              "top-1/2 right-3 z-10 absolute -translate-y-1/2",
                              "flex items-center justify-center",
                              "h-6 w-6 rounded-sm",
                              "hover:bg-accent hover:text-accent-foreground",
                              "transition-colors",
                              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                           )}
                           aria-label="Add custom value"
                        >
                           <Icon icon="plus" className="w-4 h-4" />
                        </button>
                     )}
                  </div>
                  <CommandList ref={listRef}>
                     <CommandEmpty>{emptyMessage}</CommandEmpty>
                     {isGrouped && groups ? (
                        // Render grouped options with separators between non-empty groups
                        groups
                           .map((group) => {
                              const groupFilteredOptions = group.options.filter((option) =>
                                 filteredOptions.some((fo) => fo.value === option.value)
                              );
                              return {group, groupFilteredOptions};
                           })
                           .filter(({groupFilteredOptions}) => groupFilteredOptions.length > 0)
                           .map(({group, groupFilteredOptions}, groupIndex) => (
                              <React.Fragment key={`${group.label}-${groupIndex}`}>
                                 {groupIndex > 0 && <CommandSeparator className="bg-foreground/10" />}
                                 <CommandGroup
                                    heading={group.label || undefined}
                                    className="[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:text-400 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1"
                                 >
                                    {groupFilteredOptions.map((option) => {
                                       const optionKey = getOptionKey(option);
                                       const isSelected = value === option.value;
                                       return (
                                          <CommandItem
                                             key={optionKey}
                                             value={optionKey}
                                             data-combobox-value={option.value}
                                             aria-selected={isSelected}
                                             className={cn(
                                                "px-2.5 py-1.5 text-sm",
                                                isSelected && "bg-primary/10",
                                                "hover:bg-primary/10",
                                                !isSelected && "hover:cursor-pointer"
                                             )}
                                             onSelect={() => {
                                                if (!isControlled) {
                                                   setUncontrolledValue(option.value);
                                                }
                                                setSearchValue("");
                                                setOpen(false);
                                                onChange(option.value);
                                             }}
                                          >
                                             {option.icon && (
                                                <span className="shrink-0">{option.icon}</span>
                                             )}
                                             <span className="min-w-0 truncate">{option.label}</span>
                                             <span
                                                className={cn(
                                                   "ml-auto shrink-0 flex items-center justify-center w-4 h-4 [&_svg]:!w-3 [&_svg]:!h-3",
                                                   isSelected ? "opacity-100 text-primary" : "opacity-0"
                                                )}
                                             >
                                                <Icon icon="check" />
                                             </span>
                                          </CommandItem>
                                       );
                                    })}
                                 </CommandGroup>
                              </React.Fragment>
                           ))
                     ) : (
                        // Render flat options (original behavior)
                        <CommandGroup>
                           {filteredOptions.map((option) => {
                              const optionKey = getOptionKey(option);
                              const isSelected = value === option.value;
                              return (
                                 <CommandItem
                                    key={optionKey}
                                    value={optionKey}
                                    data-combobox-value={option.value}
                                    aria-selected={isSelected}
                                    className={cn(
                                       "px-2.5 py-1.5 text-sm",
                                       isSelected && "bg-primary/10",
                                       "hover:bg-primary/10",
                                       !isSelected && "hover:cursor-pointer"
                                    )}
                                    onSelect={() => {
                                       if (!isControlled) {
                                          setUncontrolledValue(option.value);
                                       }
                                       setSearchValue("");
                                       setOpen(false);
                                       onChange(option.value);
                                    }}
                                 >
                                    {option.icon && <span className="shrink-0">{option.icon}</span>}
                                    <span className="min-w-0 truncate">{option.label}</span>
                                    <Icon
                                       icon="check"
                                       className={cn(
                                          "ml-auto w-3 h-3 shrink-0",
                                          isSelected ? "opacity-100 text-primary" : "opacity-0"
                                       )}
                                    />
                                 </CommandItem>
                              );
                           })}
                        </CommandGroup>
                     )}
                  </CommandList>
               </Command>
            </PopoverContent>
         </Popover>
      </div>
   );
}
